'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { type CSSProperties, type FC, useMemo, useState } from 'react';
import { StorageImageNew } from '@/components/atoms/StorageImage';
import { cn } from '@/libs/utils';
import { Button } from '@/shared/components/Button';
import imgFallBack from '../../../../public/images/auction_no_img.png';
import type { ExperienceCardProps } from './types';
const MY_BIDS_BID_BLOCK_BG = 'linear-gradient(268.96405083374475deg, rgba(61, 42, 168, 0) 0.37313%, rgba(81, 114, 232, 0.7) 99.621%), linear-gradient(90deg, rgba(49, 50, 75, 0.35) 0%, rgba(49, 50, 75, 0.35) 100%)';
const MY_BIDS_BID_BLOCK_BG_MOBILE = 'linear-gradient(-89.96872378445875deg, rgba(61, 42, 168, 0) 0%, rgba(81, 114, 232, 0.7) 99.995%), linear-gradient(90deg, rgba(49, 50, 75, 0.35) 0%, rgba(49, 50, 75, 0.35) 100%)';
const OUTBID_BADGE_BG_DESKTOP = 'linear-gradient(268.8937088095531deg, rgba(81, 114, 232, 0.8) 0.22062%, rgb(61, 42, 168) 99.779%), linear-gradient(90deg, rgba(49, 50, 75, 0.35) 0%, rgba(49, 50, 75, 0.35) 100%)';
const OUTBID_BADGE_BG_MOBILE = 'linear-gradient(269.4683777819953deg, rgba(81, 114, 232, 0.7) 0.16321%, rgb(61, 42, 168) 99.837%), linear-gradient(90deg, rgba(49, 50, 75, 0.35) 0%, rgba(49, 50, 75, 0.35) 100%)';
type BaseExperienceCardProps = Pick<ExperienceCardProps, 'ee_name' | 'ee_img' | 'id' | 'auction' | 'myLastBid' | 'myBidStatus'> & {
  celebrity: {
    first_name: string;
    last_name: string;
  };
  imageComponent?: React.ReactNode;
  className?: string;
  slots?: {
    top?: React.ReactNode;
    bottom?: React.ReactNode;
  };
};
interface MyLastBidSectionProps {
  amount: number;
  currency: string;
  placedAt: string;
  bidStatus?: string;
  locale: string;
}

/**
 * @description Block “Your last bid” — desktop: stacked; mobile: label+date | amount+currency (Figma My Bids).
 */
const MyLastBidSection: FC<MyLastBidSectionProps> = t0 => {
  const $ = _c(49);
  const {
    amount,
    currency,
    placedAt,
    bidStatus,
    locale
  } = t0;
  const t = useTranslations("Settings.MyAccount.MyExperiencesPage.BidsCard");
  const isOutbid = bidStatus === "OUTBID";
  let t1;
  if ($[0] !== locale || $[1] !== placedAt) {
    bb0: try {
      const d = new Date(placedAt);
      if (Number.isNaN(d.getTime())) {
        t1 = "";
        break bb0;
      }
      t1 = new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short"
      }).format(d);
    } catch {
      t1 = "";
    }
    $[0] = locale;
    $[1] = placedAt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const formattedDate = t1;
  const t2 = Number(amount);
  let t3;
  if ($[3] !== t2) {
    t3 = t2.toLocaleString("en-US");
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const formattedAmount = t3;
  const statusRingClass = bidStatus === "WINNING" ? "ring-1 ring-emerald-500/35" : "";
  let t4;
  if ($[5] !== isOutbid) {
    t4 = isOutbid ? {
      backgroundColor: "#31324B"
    } : {
      backgroundImage: MY_BIDS_BID_BLOCK_BG_MOBILE
    };
    $[5] = isOutbid;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const surfaceMobile = t4;
  let t5;
  if ($[7] !== isOutbid) {
    t5 = isOutbid ? {
      backgroundColor: "#31324B"
    } : {
      backgroundImage: MY_BIDS_BID_BLOCK_BG
    };
    $[7] = isOutbid;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const surfaceDesktop = t5;
  let t6;
  if ($[9] !== formattedAmount) {
    t6 = <span className="whitespace-nowrap text-[24px] font-semibold leading-[32px] text-[#EBECFE]">{formattedAmount}</span>;
    $[9] = formattedAmount;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== currency) {
    t7 = <span className="h-[25px] w-[46px] text-[16px] font-normal leading-[24px] text-[rgba(190,192,238,0.6)]">{currency}</span>;
    $[11] = currency;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div className="flex items-end gap-1">{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const amountCurrencyDesktop = t8;
  let t9;
  if ($[16] !== formattedAmount) {
    t9 = <span className="whitespace-nowrap text-[16px] font-bold leading-[24px] text-[#EBECFE]">{formattedAmount}</span>;
    $[16] = formattedAmount;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== currency) {
    t10 = <span className="w-[40px] text-right text-[14px] font-normal leading-[20px] text-[rgba(190,192,238,0.8)]">{currency}</span>;
    $[18] = currency;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className="flex shrink-0 flex-col items-end gap-[2px]">{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const amountCurrencyMobile = t11;
  let t12;
  if ($[23] !== formattedDate) {
    t12 = formattedDate !== "" ? <p className="text-[14px] font-medium leading-[1.2] text-[rgba(189,201,246,0.8)]">{formattedDate}</p> : null;
    $[23] = formattedDate;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const dateDesktop = t12;
  let t13;
  if ($[25] !== formattedDate) {
    t13 = formattedDate !== "" ? <p className="text-[12px] font-medium leading-[18px] text-[rgba(189,201,246,0.8)]">{formattedDate}</p> : null;
    $[25] = formattedDate;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const dateMobile = t13;
  let t14;
  if ($[27] !== statusRingClass) {
    t14 = cn("w-full overflow-hidden rounded-[16px]", statusRingClass);
    $[27] = statusRingClass;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== amountCurrencyMobile || $[30] !== dateMobile || $[31] !== isOutbid || $[32] !== t) {
    t15 = isOutbid ? <div className="flex w-full flex-row items-center gap-[8px]"><div className="flex min-h-[48px] min-w-0 flex-1 flex-col justify-between"><div className="flex flex-row items-center gap-[6px]"><span className="inline-flex h-[24px] shrink-0 items-center justify-center rounded-[6px] px-[8px] text-[12px] font-semibold leading-[1.2] text-[#EBECFE] backdrop-blur-[2px]" style={{
            backgroundImage: OUTBID_BADGE_BG_MOBILE
          }}>{t("Outbid")}</span><p className="text-[16px] font-semibold leading-[24px] text-[#EBECFE]">{t("YourLastBid")}</p></div>{dateMobile}</div>{amountCurrencyMobile}</div> : <div className="flex w-full flex-row items-center gap-[8px]"><div className="flex min-h-[48px] min-w-0 flex-1 flex-col justify-between"><p className="text-[16px] font-semibold leading-[24px] text-[#EBECFE]">{t("YourLastBid")}</p>{dateMobile}</div>{amountCurrencyMobile}</div>;
    $[29] = amountCurrencyMobile;
    $[30] = dateMobile;
    $[31] = isOutbid;
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== surfaceMobile || $[35] !== t15) {
    t16 = <div className="px-[16px] py-[12px] backdrop-blur-[2px] lg:hidden" style={surfaceMobile}>{t15}</div>;
    $[34] = surfaceMobile;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== amountCurrencyDesktop || $[38] !== dateDesktop || $[39] !== isOutbid || $[40] !== t) {
    t17 = isOutbid ? <div className="flex w-full flex-col gap-[12px]"><div className="flex w-full flex-row items-start gap-[12px]"><div className="flex min-w-0 flex-1 flex-col gap-0"><p className="text-[16px] font-semibold leading-[24px] text-[#EBECFE]">{t("YourLastBid")}</p>{amountCurrencyDesktop}</div><span className="inline-flex h-[28px] shrink-0 items-center justify-center rounded-[8px] px-[16px] text-[12px] font-semibold leading-[1.2] text-[#EBECFE] backdrop-blur-[2px]" style={{
          backgroundImage: OUTBID_BADGE_BG_DESKTOP
        }}>{t("Outbid")}</span></div>{dateDesktop}</div> : <div className="flex w-full flex-col gap-[12px]"><div className="flex flex-col gap-0"><p className="text-[16px] font-semibold leading-[24px] text-[#EBECFE]">{t("YourLastBid")}</p>{amountCurrencyDesktop}</div>{dateDesktop}</div>;
    $[37] = amountCurrencyDesktop;
    $[38] = dateDesktop;
    $[39] = isOutbid;
    $[40] = t;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== surfaceDesktop || $[43] !== t17) {
    t18 = <div className="hidden p-[12px] backdrop-blur-[2px] lg:block" style={surfaceDesktop}>{t17}</div>;
    $[42] = surfaceDesktop;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t14 || $[46] !== t16 || $[47] !== t18) {
    t19 = <div className={t14}>{t16}{t18}</div>;
    $[45] = t14;
    $[46] = t16;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  return t19;
};
export const BaseExperienceCard: FC<BaseExperienceCardProps> = t0 => {
  const $ = _c(53);
  const {
    ee_name,
    ee_img,
    id,
    auction,
    celebrity: t1,
    myLastBid,
    myBidStatus,
    imageComponent,
    className,
    slots
  } = t0;
  const {
    first_name,
    last_name
  } = t1;
  const t = useTranslations("experiences");
  const tBidsCard = useTranslations("Settings.MyAccount.MyExperiencesPage.BidsCard");
  const locale = useLocale();
  const [_hasError, setHasError] = useState(false);
  let t2;
  if ($[0] !== ee_name) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = / /g;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = ee_name.replace(t3, "-");
    $[0] = ee_name;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `/${locale}/auctions/${id}-${t2}`;
  const t4 = `relative group bg-[#31324B59] bg-[linear-gradient(180deg,rgba(53,46,115,0)_0%,rgba(53,46,115,0.15)_100%)] p-2 flex flex-col gap-2 items-center h-full rounded-[20px] ${className}`;
  let t5;
  if ($[3] !== slots) {
    t5 = slots?.top && <div className="absolute w-full top-0 right-0 z-10">{slots.top}</div>;
    $[3] = slots;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== _hasError || $[6] !== ee_img || $[7] !== ee_name || $[8] !== imageComponent) {
    t6 = imageComponent ? imageComponent : <StorageImageNew src={_hasError ? imgFallBack.src : `${ee_img}`} alt={ee_name} aspectRatio="1/1" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" withBlur={true} onError={() => setHasError(true)} sources={[{
      media: "(min-width: 1280px)",
      width: 384,
      height: 384,
      resize: "cover",
      dprs: [1, 2]
    }, {
      media: "(min-width: 768px)",
      width: 343,
      height: 343,
      resize: "cover",
      dprs: [1, 2]
    }, {
      media: "(min-width: 0px)",
      width: 156,
      height: 156,
      resize: "cover",
      dprs: [1, 2]
    }]} />;
    $[5] = _hasError;
    $[6] = ee_img;
    $[7] = ee_name;
    $[8] = imageComponent;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== slots) {
    t7 = slots?.bottom && <div className="absolute w-full bottom-0 right-0 z-10">{slots.bottom}</div>;
    $[10] = slots;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = <div className="relative rounded-[20px] aspect-[1/1] w-full overflow-hidden flex-shrink-0">{t5}{t6}{t7}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="relative size-[14px] md:size-4"><div className="absolute inset-[3px] bg-white rounded-full" /><Image src="/icons/verified-blue.svg" alt="Verified" width={14} height={14} className="relative size-[14px] md:size-4" /></div>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `${first_name} ${last_name}`;
  let t11;
  if ($[17] !== t10) {
    t11 = <div className="flex items-center w-full justify-start gap-1.5">{t9}<span className="font-inter text-[16px] md:text-[14px] font-semibold text-[#EBECFE] lg:text-base leading-[120%] truncate max-w-[100%]">{t10}</span></div>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== ee_name) {
    t12 = <h5 className="font-inter text-[18px] line-clamp-2 tablet:text-lg font-bold leading-[130%] uppercase text-[#8082B2]">{ee_name}</h5>;
    $[19] = ee_name;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = Number(auction?.current_price);
  let t14;
  if ($[21] !== t13) {
    t14 = t13.toLocaleString("en-US");
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <span className="leading-[110%] text-xl font-inter md:text-[24px] font-semibold text-[#EBECFE] truncate">{t14}</span>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className=" self-center text-md md:text-[20px] font-normal text-[#BEC0EE99]">USDC</span>;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t15) {
    t17 = <div className="flex items-center gap-1">{t15}{t16}</div>;
    $[26] = t15;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== locale || $[29] !== myBidStatus || $[30] !== myLastBid) {
    t18 = myLastBid ? <MyLastBidSection amount={myLastBid.amount} currency={myLastBid.currency} placedAt={myLastBid.placedAt} bidStatus={myBidStatus} locale={locale} /> : null;
    $[28] = locale;
    $[29] = myBidStatus;
    $[30] = myLastBid;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== myBidStatus || $[33] !== t || $[34] !== tBidsCard) {
    t19 = myBidStatus === "OUTBID" ? tBidsCard("PlaceAnotherBid") : t("buy");
    $[32] = myBidStatus;
    $[33] = t;
    $[34] = tBidsCard;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t19) {
    t20 = <Button variant="whiteSecondary" size="48_56" className="w-full  lg:text-[28px] max-h-[56px]" textClassName="text-[18px] font-bold leading-[130%] uppercase">{t19}</Button>;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t17 || $[39] !== t18 || $[40] !== t20) {
    t21 = <div className="mt-auto md:pt-2 flex flex-col gap-3 md:gap-4">{t17}{t18}{t20}</div>;
    $[38] = t17;
    $[39] = t18;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t11 || $[43] !== t12 || $[44] !== t21) {
    t22 = <div className="flex flex-col gap-2 w-full p-1.5 flex-1">{t11}{t12}{t21}</div>;
    $[42] = t11;
    $[43] = t12;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t22 || $[47] !== t4 || $[48] !== t8) {
    t23 = <div className={t4}>{t8}{t22}</div>;
    $[46] = t22;
    $[47] = t4;
    $[48] = t8;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t23 || $[51] !== t3) {
    t24 = <Link href={t3} className="h-full block">{t23}</Link>;
    $[50] = t23;
    $[51] = t3;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  return t24;
};